import { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import { buildTabIdKey, AUTH_KEYS } from "../constants/storageKeys";
import { clearFinishedExamData } from "../utils/examStorageCleaner";

// WebSocket event types
const EVENTS = {
  EXAM_JOIN: "exam:join",
  EXAM_JOINED: "exam:joined",
  EXAM_LEAVE: "exam:leave",
  EXAM_HEARTBEAT: "exam:heartbeat",
  EXAM_HEARTBEAT_ACK: "exam:heartbeat_ack",
  EXAM_TAKEOVER: "exam:takeover",
  EXAM_TAKEOVER_SUCCESS: "exam:takeover_success",
  EXAM_RECONNECT: "exam:reconnect",
  EXAM_RECONNECT_SUCCESS: "exam:reconnect_success",
  EXAM_KICKED: "exam:kicked",
  EXAM_CONFLICT: "exam:conflict",
  EXAM_LOCK_EXPIRED: "exam:lock_expired",
  SESSION_TERMINATED: "session:terminated",
  ERROR: "error",
};

// Tab lock status
export const TAB_LOCK_STATUS = {
  CONNECTING: "connecting",
  JOINED: "joined",
  CONFLICT: "conflict",
  KICKED: "kicked",
  DISCONNECTED: "disconnected",
  SESSION_TERMINATED: "session_terminated",
  ERROR: "error",
};

/**
 * Hook for managing exam tab lock via WebSocket
 * Enforces single-tab policy for exam sessions
 * @param {string} productSessionId - The product session ID
 * @param {string} userProductRegistrationId - The user product registration ID
 * @param {boolean} enabled - Whether tab lock is enabled
 * @param {function} onTakeoverSuccess - Callback when takeover succeeds (to re-init resources)
 */
export const useExamTabLock = (productSessionId, userProductRegistrationId, enabled = true, onTakeoverSuccess = null) => {
  const [status, setStatus] = useState(TAB_LOCK_STATUS.CONNECTING);
  const [tabId, setTabId] = useState(null);
  const [conflict, setConflict] = useState(null);
  const [kickedMessage, setKickedMessage] = useState(null);
  const [isConnected, setIsConnected] = useState(false);

  const socketRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const onTakeoverSuccessRef = useRef(onTakeoverSuccess);

  // Keep the callback ref updated
  useEffect(() => {
    onTakeoverSuccessRef.current = onTakeoverSuccess;
  }, [onTakeoverSuccess]);

  // Get stored tabId for reconnection (use sessionStorage - per tab, not shared)
  const getStoredTabId = useCallback(() => {
    return sessionStorage.getItem(buildTabIdKey(productSessionId));
  }, [productSessionId]);

  // Store tabId for reconnection (use sessionStorage - per tab, not shared)
  const storeTabId = useCallback((id) => {
    if (id) {
      sessionStorage.setItem(buildTabIdKey(productSessionId), id);
    } else {
      sessionStorage.removeItem(buildTabIdKey(productSessionId));
    }
  }, [productSessionId]);

  // Start heartbeat
  const startHeartbeat = useCallback((interval, currentTabId) => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }

    heartbeatIntervalRef.current = setInterval(() => {
      if (socketRef.current?.connected && currentTabId) {
        socketRef.current.emit(EVENTS.EXAM_HEARTBEAT, { tabId: currentTabId });
      }
    }, interval);
  }, []);

  // Stop heartbeat
  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Request takeover
  const takeover = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(EVENTS.EXAM_TAKEOVER, { productSessionId });
    }
  }, [productSessionId]);

  // Leave exam (release lock)
  const leave = useCallback(() => {
    if (socketRef.current?.connected) {
      socketRef.current.emit(EVENTS.EXAM_LEAVE, { productSessionId });
    }
    stopHeartbeat();
    storeTabId(null);
    setTabId(null);
  }, [productSessionId, stopHeartbeat, storeTabId]);

  // Initialize WebSocket connection
  useEffect(() => {
    if (!enabled || !productSessionId) {
      // console.log('[TAB_LOCK_DEBUG] WebSocket NOT connecting - enabled:', enabled, 'productSessionId:', productSessionId);
      return;
    }

    const token = localStorage.getItem(AUTH_KEYS.TOKEN);
    if (!token) {
      setStatus(TAB_LOCK_STATUS.ERROR);
      return;
    }

    // Socket.io connects to base URL, not the API path
    const wsUrl = process.env.REACT_APP_API_URL.replace(/\/api\/v\d+$/, '');
    const socket = io(wsUrl, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    // Connection established
    socket.on("connect", () => {
      setIsConnected(true);
      reconnectAttemptRef.current = 0;

      // Check if we have a stored tabId for reconnection
      const storedTabId = getStoredTabId();
      if (storedTabId) {
        // Try to reconnect with existing tabId
        socket.emit(EVENTS.EXAM_RECONNECT, {
          tabId: storedTabId,
          productSessionId,
        });
      } else {
        // Join as new tab
        socket.emit(EVENTS.EXAM_JOIN, {
          productSessionId,
          userProductRegistrationId,
        });
      }
    });

    // Successfully joined
    socket.on(EVENTS.EXAM_JOINED, (data) => {
      // console.log('[TAB_LOCK_DEBUG] Received exam:joined - Lock acquired!', data);
      setStatus(TAB_LOCK_STATUS.JOINED);
      setTabId(data.tabId);
      storeTabId(data.tabId);
      setConflict(null);
      startHeartbeat(data.heartbeatInterval || 15000, data.tabId);
    });

    // Conflict - another tab is active
    socket.on(EVENTS.EXAM_CONFLICT, (data) => {
      console.log('[TAB_LOCK_DEBUG] Received exam:conflict - Another tab has the lock!', data);
      setStatus(TAB_LOCK_STATUS.CONFLICT);
      setConflict(data.existingLock);
    });

    // Takeover successful
    socket.on(EVENTS.EXAM_TAKEOVER_SUCCESS, (data) => {
      setStatus(TAB_LOCK_STATUS.JOINED);
      setTabId(data.tabId);
      storeTabId(data.tabId);
      setConflict(null);
      startHeartbeat(data.heartbeatInterval || 15000, data.tabId);

      // Bug Fix #3: Trigger resource initialization callback after takeover
      // This allows the exam page to re-initialize resources
      if (onTakeoverSuccessRef.current) {
        onTakeoverSuccessRef.current();
      }
    });

    // Reconnect successful
    socket.on(EVENTS.EXAM_RECONNECT_SUCCESS, (data) => {
      setStatus(TAB_LOCK_STATUS.JOINED);
      setTabId(data.tabId);
      startHeartbeat(data.heartbeatInterval || 15000, data.tabId);
    });

    // Lock expired during reconnect
    socket.on(EVENTS.EXAM_LOCK_EXPIRED, (data) => {
      storeTabId(null);
      // Try to join fresh
      socket.emit(EVENTS.EXAM_JOIN, {
        productSessionId,
        userProductRegistrationId,
      });
    });

    // Kicked by another tab
    socket.on(EVENTS.EXAM_KICKED, (data) => {
      setStatus(TAB_LOCK_STATUS.KICKED);
      setKickedMessage(data.message);
      stopHeartbeat();
      storeTabId(null);

      // Bug Fix #1: Clear all exam data from localStorage when kicked
      // This ensures the old browser doesn't retain stale exam data
      clearFinishedExamData().catch((err) => {
        console.error("Failed to clear exam data after kick:", err);
      });
    });

    // Session terminated (logged out from another device)
    socket.on(EVENTS.SESSION_TERMINATED, (data) => {
      setStatus(TAB_LOCK_STATUS.SESSION_TERMINATED);
      stopHeartbeat();
      storeTabId(null);
      // Clear auth and redirect
      localStorage.removeItem(AUTH_KEYS.TOKEN);
      localStorage.removeItem(AUTH_KEYS.SESSION_ID);
      window.location.href = process.env.REACT_APP_MAIN_APP_URL + "/public/auth/sign-in?reason=session_terminated";
    });

    // Heartbeat acknowledged
    socket.on(EVENTS.EXAM_HEARTBEAT_ACK, (data) => {
      // Heartbeat confirmed
    });

    // Error
    socket.on(EVENTS.ERROR, (data) => {
      // Error handled silently
    });

    // Connection error
    socket.on("connect_error", (error) => {
      console.log('[TAB_LOCK_DEBUG] Socket connect_error:', error.message);
    });

    // Disconnection
    socket.on("disconnect", (reason) => {
      setIsConnected(false);
      if (reason !== "io client disconnect") {
        setStatus(TAB_LOCK_STATUS.DISCONNECTED);
      }
    });

    // Cleanup
    return () => {
      stopHeartbeat();
      if (socket.connected) {
        socket.emit(EVENTS.EXAM_LEAVE, { productSessionId });
        socket.disconnect();
      }
    };
  }, [
    enabled,
    productSessionId,
    userProductRegistrationId,
    getStoredTabId,
    storeTabId,
    startHeartbeat,
    stopHeartbeat,
  ]);

  return {
    status,
    tabId,
    conflict,
    kickedMessage,
    isConnected,
    takeover,
    leave,
  };
};

export default useExamTabLock;
